import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  ReplaySubject,
  startWith,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { UpdateBuchungserstellAntragDto } from './api/models';
import { BuchungserstellAntragsApiService } from './api/services';
import { PAGINATION_HEADER_NAME, PaginationHeader } from './pagination-header';

@Injectable({
  providedIn: 'root',
})
export class BuchungserstellAntraegeStore {
  private readonly _currentPage$ = new BehaviorSubject(1);
  private readonly _currentPageSize$ = new BehaviorSubject(15);
  private readonly _totalCount$ = new ReplaySubject<number>();
  private readonly _mitarbeiterFilter$ = new BehaviorSubject('');
  private readonly _showBearbeitete$ = new BehaviorSubject(false);
  private readonly _reload$ = new Subject();

  public readonly currentPage$ = this._currentPage$.pipe(map((x) => x - 1));
  public readonly currentPageSize$ = this._currentPageSize$.asObservable();
  public readonly totalCount$ = this._totalCount$.asObservable();
  public readonly mitarbeiterFilter$ = this._mitarbeiterFilter$.asObservable();
  public readonly showBearbeitete$ = this._showBearbeitete$.asObservable();

  public readonly countOpenErstellAntraegeMonteure$ = this._reload$.pipe(
    startWith(null),
    switchMap(() =>
      this._api.getOffeneBuchungserstellAntraegeCount$Json({
        withoutMonteure: false,
      }),
    ),
  );
  public readonly countOpenErstellAntraegeWithoutMonteure$ = this._reload$.pipe(
    startWith(null),
    switchMap(() =>
      this._api.getOffeneBuchungserstellAntraegeCount$Json({
        withoutMonteure: true,
      }),
    ),
  );

  public set currentPage(page: number) {
    this._currentPage$.next(page + 1);
  }

  public set currentPageSize(pageSize: number) {
    if (pageSize !== this._currentPageSize$.value) {
      this.currentPage = 0;
    }
    this._currentPageSize$.next(pageSize);
  }

  public set mitarbeiterFilter(filterString: string) {
    if (this._mitarbeiterFilter$.value != filterString) {
      this.currentPage = 0;
    }
    this._mitarbeiterFilter$.next(filterString.toLowerCase());
  }

  public set showBearbeitete(shouldDisplay: boolean) {
    this._showBearbeitete$.next(shouldDisplay);
  }

  public readonly erstellAntraege$ = combineLatest([
    this._currentPage$.pipe(distinctUntilChanged()),
    this._currentPageSize$.pipe(distinctUntilChanged()),
    this._mitarbeiterFilter$.pipe(
      map((x) => (x === '' ? undefined : x)),
      debounceTime(500),
    ),
    this._showBearbeitete$.pipe(distinctUntilChanged()),
    this._reload$.pipe(startWith(null)),
  ]).pipe(
    switchMap(([index, size, mitarbeiterFilter, showBearbeitete]) => {
      return this._api.getAllErstellAntraegePaginated$Json$Response({
        PageNumber: index,
        PageSize: size,
        MitarbeiterName: mitarbeiterFilter,
        ZeigeBearbeitete: showBearbeitete,
      });
    }),
    tap((response) => {
      const header = response.headers.get(PAGINATION_HEADER_NAME);
      if (!header) {
        throw new Error('No Pagination Headers recieved');
      }
      const contents = JSON.parse(header) as PaginationHeader;
      this._totalCount$.next(contents.TotalCount);
    }),
    map((response) => response.body),
  );

  constructor(private readonly _api: BuchungserstellAntragsApiService) {}

  public updateOneAntrag(id: string, data: UpdateBuchungserstellAntragDto) {
    return this._api
      .updateOneBuchungserstellAntrag$Json({
        antragsId: id,
        body: data,
      })
      .pipe(tap(() => this._reload$.next(null)));
  }
}
